/**
 * The wall (variant A): months of tiles, newest first, read a page at a time; the end of it —
 * the next page's place or the line after the last — is `exercise-clip`'s `ClipOlderPages`.
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ClipOlderPages, ClipTileGrid, ClipTileRowSkeleton } from '~/entities/exercise-clip';
import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import { EmptyState } from '~/shared/ui/EmptyState';
import { VideoMarkIcon } from '~/shared/ui/EmptyStateIcons';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { RecordingsState } from '../lib/useRecordings';

import styles from './RecordingsScreen.module.css';

const SKELETON_TILES = 6;

interface RecordingsWallProps {
  readonly state: RecordingsState;
  readonly gate: SkeletonGate;
  readonly onOpenClip: (clipId: number) => void;
}

export function RecordingsWall({ state, gate, onOpenClip }: RecordingsWallProps) {
  const { t } = useTranslation();
  const region = useRef<HTMLElement>(null);
  const { view } = state;
  const empty = view.months.length === 0;

  return (
    <section
      aria-busy={gate.waiting}
      aria-label={t('web:recordings.wallLabel')}
      className={styles.wall}
      ref={region}
      tabIndex={-1}
    >
      {gate.waiting ? (
        <>
          <span className={hidden.srOnly}>{t('web:recordings.loading')}</span>
          <ClipTileRowSkeleton drawn={gate.skeleton} month oneRow tiles={SKELETON_TILES} />
        </>
      ) : state.wallFailed ? (
        <p className={`${styles.notice} ${gate.reveal}`} role="alert">
          {t('web:recordings.failed')}{' '}
          <button
            className={inlineButton.inlineTextButton}
            onClick={() => {
              state.retry();
              region.current?.focus();
            }}
            type="button"
          >
            {t('web:recordings.retry')}
          </button>
        </p>
      ) : empty ? (
        <div className={`${styles.empty} ${gate.reveal}`}>
          <EmptyState
            hint={t('web:recordings.emptyHint')}
            mark={<VideoMarkIcon />}
            title={t('web:recordings.empty')}
            tone="neutral"
          />
        </div>
      ) : (
        <div className={`${styles.wallBody} ${gate.reveal}`}>
          <ClipTileGrid months={view.months} onOpenClip={onOpenClip} />
          <ClipOlderPages older={state.older} />
        </div>
      )}
    </section>
  );
}
